import { Fragment, ReactNode, useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useRegistryAccess } from '../portal-access/useRegistryAccess';
import { Box, Button, CircularProgress, Link, Paper, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ManifestBundle, RegistryTable, TablePrTicket, lhRegistryApiRef } from './api';
import { MANIFEST_STEPS, errorText, manifestLabel, manifestStep, manifestTone } from './manifestLabel';
import { Pill, Tone } from './ui';

const CI_LABEL: Record<string, string> = { pending: '대기', success: '통과', failure: '실패', unknown: '알 수 없음' };
const MERGEABLE_LABEL: Record<string, string> = { clean: '충돌 없음', dirty: '충돌', unknown: '확인 중' };
const MONO = "'Roboto Mono', monospace";

const PrLink = ({ url, n, state }: { url: string | null; n: number | null; state?: string | null }) =>
  url ? <><Link href={url} target="_blank" rel="noreferrer" style={{ fontFamily: MONO }}>#{n} ↗</Link>{state ? ` (${state})` : ''}</> : <>—</>;

/**
 * 상세의 "manifest PR" 카드 (설계 14.7). 테이블이 속한 티켓마다 상태 배지·진행 단계·오류 안내·grp/브랜치/dev PR/CI/prod PR 과
 * 관리자 조치 버튼(Manifest 검토 탭과 같은 API). prod PR 주소는 서버가 admin 사용자에게만 준다(그 밖에는 null).
 */
export const ManifestPrCard = ({ table, readOnly, onChanged }: { table: RegistryTable; readOnly: boolean; onChanged: () => void }) => {
  const api = useApi(lhRegistryApiRef);
  const { canAdmin } = useRegistryAccess();
  const [tickets, setTickets] = useState<TablePrTicket[]>();
  const [bundles, setBundles] = useState<Record<number, ManifestBundle>>({});
  const [error, setError] = useState<{ message: string; hide: boolean }>();
  const [busy, setBusy] = useState(false);
  const [actError, setActError] = useState<string>();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .tablePrs(table.id)
      .then(async r => {
        if (cancelled) return;
        setTickets(r.tickets);
        // 소속 묶음의 대표 티켓·같은 묶음의 다른 티켓은 묶음 상세에서 가져온다 (보조 정보 — 실패해도 카드는 보인다)
        const ids = [...new Set(r.tickets.map(t => t.bundle_id).filter((x): x is number => !!x))];
        const got = await Promise.all(ids.map(id => api.manifestBundle(id).catch(() => undefined)));
        if (!cancelled) setBundles(Object.fromEntries(got.flatMap((b, i) => (b ? [[ids[i], b]] : []))));
      })
      // GitHub 설정이 없으면(503 MANIFEST_DISABLED) 카드를 통째로 숨긴다
      .catch(e => !cancelled && setError({ message: e.message, hide: (e as { code?: string }).code === 'MANIFEST_DISABLED' }));
    return () => {
      cancelled = true;
    };
  }, [api, table.id, table.version, tick]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActError(undefined);
    try {
      await fn();
      setTick(x => x + 1);
      onChanged();
    } catch (e) {
      setActError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (error) return error.hide ? null : <Typography variant="caption" color="error">{error.message}</Typography>;
  if (!tickets) return <CircularProgress size={20} />;
  if (!tickets.length) return null;

  return (
    <>
      {tickets.map(t => {
        const lab = {
          status: t.display_status, ciState: t.dev_ci_state, mergeable: t.dev_mergeable, botPrNumber: t.bot_pr_number, botPrState: t.bot_pr_state,
          errorCode: t.bundle_error_code ?? t.error_code,
        };
        const tone = manifestTone(lab);
        const at = manifestStep(lab);
        // 지난 단계는 녹, 현재 단계는 차단·실패면 그 톤 아니면 채움, 남은 단계는 회색
        const stepTone = (i: number): Tone => {
          if (tone === 'ok' || i < at) return 'ok';
          if (i !== at) return 'neutral';
          return tone === 'warn' || tone === 'error' ? tone : 'solid';
        };
        const b = t.bundle_id ? bundles[t.bundle_id] : undefined;
        const others = (b?.tickets as { ticketKey: string }[] | undefined)?.map(x => x.ticketKey).filter(k => k !== t.ticket_key) ?? [];
        // 조치는 Manifest 검토 탭의 버튼과 같은 조건·API 다. 묶음 실패는 묶음 재시도, 묶음 밖 실패 티켓은 다시 대기
        const bid = t.bundle_id;
        let action: [string, () => Promise<unknown>] | undefined;
        if (bid && t.display_status === 'foreign_commit') action = ['확인 후 계속', () => api.manifestResume(bid)];
        // promote 는 registry.admin 만 — 그 밖 단계에서는 조치를 보이지 않는다(서버도 403)
        else if (bid && (['main_merged', 'promote_blocked', 'promote_requested'].includes(t.display_status) || lab.errorCode === 'PROD_PR_CLOSED'))
          action = !canAdmin ? undefined : [t.display_status === 'main_merged' ? '/promote 요청' : '/promote 재요청', () => api.manifestPromote(bid)];
        else if (bid && (t.display_status === 'failed' || lab.errorCode === 'COMMIT_STALLED')) action = ['다시 시도', () => api.manifestRetry(bid)];
        else if (t.status === 'failed') action = ['다시 대기', () => api.manifestRetryTicket(t.id)];
        const [actLabel, actFn] = action ?? [];

        const rows: [string, ReactNode][] = [
          ['manifest grp', t.manifest_path ?? '—'],
          ['브랜치', t.branch ? <span style={{ fontFamily: MONO }}>{t.branch}</span> : '—'],
          ['dev PR', <PrLink url={t.dev_pr_url} n={t.dev_pr_number} />],
          ['CI', t.dev_pr_url ? `${CI_LABEL[t.dev_ci_state ?? 'unknown'] ?? t.dev_ci_state} · ${MERGEABLE_LABEL[t.dev_mergeable ?? 'unknown'] ?? t.dev_mergeable}` : '—'],
          ['prod PR', <PrLink url={t.promote_pr_url} n={t.promote_pr_number} state={t.promote_pr_state} />],
        ];
        if (b) rows.push(['묶음', `#${b.id} · 대표 티켓 ${b.repTicketKey}`]);
        if (others.length) rows.push(['같은 묶음', others.join(', ')]);
        if (t.bot_pr_url) rows.push(['BOT PR', <PrLink url={t.bot_pr_url} n={t.bot_pr_number} state={t.bot_pr_state} />]);
        if (t.bot_promote_pr_url) rows.push(['BOT prod PR', <PrLink url={t.bot_promote_pr_url} n={t.bot_promote_pr_number} state={t.bot_promote_pr_state} />]);

        return (
          <Paper key={t.id} variant="outlined" component="section" aria-label="manifest PR">
            <Box p={2.5} display="flex" flexDirection="column" style={{ gap: 12 }}>
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                <Typography variant="subtitle1" style={{ flex: 1 }}>
                  manifest PR <Typography component="span" variant="caption" color="textSecondary">dp-lakehouse · 근거 티켓 {t.ticket_key}</Typography>
                </Typography>
                <Pill tone={tone}>{manifestLabel(lab)}</Pill>
              </Box>
              <Box component="ol" aria-label="진행 단계" display="flex" flexWrap="wrap" m={0} p={0} style={{ gap: 6, listStyle: 'none' }}>
                {MANIFEST_STEPS.map((l, i) => (
                  <li key={l} aria-current={i === at ? 'step' : undefined}>
                    <Pill tone={stepTone(i)}>{l}</Pill>
                  </li>
                ))}
              </Box>
              {t.bot_conflict && <Alert severity="warning">묶음에 속해 있는 동안 BOT PR 이 나타났습니다 — 이 티켓을 묶음에서 제외하세요.</Alert>}
              {lab.errorCode && (
                <Alert severity={tone === 'error' ? 'error' : 'warning'}>
                  <b>{lab.errorCode}</b> · {errorText(lab.errorCode)}
                  {t.bundle_error_detail && <><br />{t.bundle_error_detail}</>}
                </Alert>
              )}
              <Box style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 16, rowGap: 6 }}>
                {rows.map(([k, v]) => (
                  <Fragment key={k}>
                    <Typography variant="body2" color="textSecondary">{k}</Typography>
                    <Typography variant="body2" component="div" style={{ wordBreak: 'break-all' }}>{v}</Typography>
                  </Fragment>
                ))}
              </Box>
              {actFn && !readOnly && (
                <Box>
                  <Button variant="contained" color="primary" size="small" disabled={busy} onClick={() => act(actFn)}>{actLabel}</Button>
                </Box>
              )}
              {actError && <Alert severity="error">{actError}</Alert>}
            </Box>
          </Paper>
        );
      })}
    </>
  );
};
